import { Router } from 'react-router-dom';
import { HelmetProvider } from '@dr.pogodin/react-helmet';
import { Provider as ReduxProvider, useSelector } from 'react-redux';
// material
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
// routes
import routes, { renderRoutes } from './routes';
// redux
import { store } from './redux/store';
// theme
import ThemeProvider from './theme';
// white-label brand
import { BrandProvider } from './contexts/BrandContext';
import BrandGate from './components/BrandGate';
import MaintenanceGate from './components/MaintenanceGate';
// components
import RtlLayout from './components/RtlLayout';
import ScrollToTop from './components/ScrollToTop';
import NotistackProvider from './components/NotistackProvider';
import JwtProvider from './components/authentication/JwtProvider';
import { EventBusProvider } from './hooks/useEventBus';
import { GeofenceProvider } from './hooks/useGeofences';
import { GOOGLE_CLIENT_ID, isDevelopment } from 'src/config';
//

import SocketIO from 'src/backend/SocketIO';
import {
  DASHBOARD_VERSION_BASELINE_KEY,
  DASHBOARD_VERSION_LATEST_KEY,
  isDashboardOutdated,
} from 'src/utils/dashboardVersion';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  Paper,
  Stack,
  Typography,
  DialogTitle,
  Snackbar,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { LoadingButton } from '@mui/lab';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { BProgress } from '@bprogress/core';
import { PATH_AUTH, PATH_DASHBOARD } from 'src/routes/paths';
import {
  getAdminImpersonation,
  isHandoffImpersonation,
} from 'src/utils/adminImpersonation';
import endAdminImpersonation from 'src/utils/endAdminImpersonation';
import React, { useEffect, useState, useCallback } from 'react';
import { getTeams as fetchMyTeams, switchTeams } from 'src/backend/user';
import { setSession } from 'src/redux/slices/auth';

import {
  QueryClientProvider,
  QueryClient,
  QueryCache,
} from '@tanstack/react-query';
import 'src/locales/i18n';
import { getDateFnsLocale } from 'src/locales/libraryLocales';
import { useTranslation } from 'react-i18next';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { history } from '../instrument';
import { firebaseSignOut } from 'src/adapters/FirestoreAdapter';

const queryClient = new QueryClient({
  queryCache: new QueryCache(),
  defaultOptions: {
    queries: {
      networkMode: 'offlineFirst',
    },
    mutations: {
      networkMode: 'offlineFirst',
    },
  },
});

// ----------------------------------------------------------------------

SocketIO.init();

// ----------------------------------------------------------------------

function SessionExpiredModal() {
  const { t } = useTranslation('translation');

  const [openModal, setOpenModal] = useState(false);

  useEffect(() => {
    function handleTokenExpiredEvent(event) {
      if (
        !window.location.pathname.includes('login') &&
        window.location.pathname !== '/apps'
      ) {
        setOpenModal(event.detail.isExpired);
      }
    }

    window.addEventListener('token-expired', handleTokenExpiredEvent);
    return () =>
      window.removeEventListener('token-expired', handleTokenExpiredEvent);
  }, []);

  // An impersonation session ends at its cap or when the super admin is
  // removed; take the admin back to their own session rather than to login.
  const impersonation = openModal ? getAdminImpersonation() : null;

  const handleSessionExpired = () => {
    setOpenModal(false);
    if (impersonation) {
      endAdminImpersonation(impersonation);
      return;
    }
    firebaseSignOut();
    window.open(PATH_AUTH.login, '_self');
  };

  return (
    <Dialog open={openModal} onClose={handleSessionExpired}>
      <DialogTitle>
        {impersonation
          ? t('admin.impersonation.ended-title')
          : t('session-expired.title')}
      </DialogTitle>
      <DialogContent>
        <Typography>
          {impersonation
            ? t('admin.impersonation.ended-message')
            : t('session-expired.message')}
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleSessionExpired} variant="outlined">
          {!impersonation
            ? t('session-expired.login')
            : isHandoffImpersonation(impersonation)
              ? t('admin.impersonation.exit')
              : t('admin.impersonation.return')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Full-screen notice shown when the backend rejects every request for
 * account-level reasons (license lockout or declined approval). Triggered by
 * the 'account-access-blocked' event dispatched from src/backend/common.js.
 */
function teamLabel(team) {
  return team.name;
}

function AccountBlockedScreen() {
  const { t } = useTranslation('translation');

  const [blocked, setBlocked] = useState(null);
  const [teams, setTeams] = useState([]);
  const [switchingTo, setSwitchingTo] = useState(null);
  const [switchError, setSwitchError] = useState(false);
  const currentTeamId = useSelector((state) => state.auth?.user?.team_id);

  useEffect(() => {
    function handleAccountBlockedEvent(event) {
      if (
        !window.location.pathname.includes('login') &&
        window.location.pathname !== '/apps'
      ) {
        setBlocked(event.detail);
      }
    }

    window.addEventListener(
      'account-access-blocked',
      handleAccountBlockedEvent
    );
    return () =>
      window.removeEventListener(
        'account-access-blocked',
        handleAccountBlockedEvent
      );
  }, []);

  useEffect(() => {
    if (!blocked) {
      return;
    }
    fetchMyTeams()
      .then((list) => setTeams(Array.isArray(list) ? list : []))
      .catch(() => setTeams([])); // fail-safe: logout-only screen
  }, [blocked]);

  const handleLogout = () => {
    firebaseSignOut();
    // Clear the session tokens too: this screen shows for a VALID but
    // blocked session, and auth init treats blocked sessions as
    // authenticated — leaving tokens behind would bounce the user
    // straight back into the blocked screen instead of the login form.
    setSession(null, null);
    window.open(PATH_AUTH.login, '_self');
  };

  const handleSwitch = (teamId) => {
    setSwitchError(false);
    setSwitchingTo(teamId);
    const refreshToken = localStorage.getItem('refresh-token');
    switchTeams(teamId, refreshToken)
      .then(({ token, refreshToken: newRefreshToken }) => {
        localStorage.removeItem('refresh-token');
        localStorage.removeItem('active-geofences');
        setSession(token, newRefreshToken);
        window.location.pathname = PATH_DASHBOARD.root;
        setTimeout(() => window.location.reload(), 500);
      })
      .catch(() => {
        setSwitchError(true);
        setSwitchingTo(null);
      });
  };

  if (!blocked) {
    return null;
  }

  const isDeclined = blocked.code === 'ACCOUNT_DECLINED';
  // The team we're blocked in is the locked/declined one; every OTHER
  // non-individual team is a way out.
  const otherTeams = teams.filter(
    (team) => !team.is_individual && team.team_id !== currentTeamId
  );

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: (theme) => theme.zIndex.snackbar + 100,
        bgcolor: 'background.default',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
      }}
    >
      <Stack spacing={3} alignItems="center" sx={{ maxWidth: 480 }}>
        <Typography variant="h3" align="center">
          {isDeclined
            ? t('account-blocked.declined-title')
            : t('account-blocked.license-locked-title')}
        </Typography>
        <Typography align="center" color="text.secondary">
          {blocked.message ||
            (isDeclined
              ? t('account-blocked.declined-message')
              : t('account-blocked.license-locked-message'))}
        </Typography>
        {otherTeams.length > 0 && (
          <Stack spacing={1} alignItems="stretch" sx={{ width: '100%' }}>
            <Typography variant="subtitle2" align="center">
              {t('account-blocked.switch-title')}
            </Typography>
            {otherTeams.map((team) => (
              <LoadingButton
                key={team.team_id}
                variant="outlined"
                disabled={
                  Boolean(team.license_locked) ||
                  (switchingTo !== null && switchingTo !== team.team_id)
                }
                loading={switchingTo === team.team_id}
                onClick={() => handleSwitch(team.team_id)}
              >
                {teamLabel(team)}
                {team.license_locked
                  ? ` — ${t('account-blocked.license-inactive')}`
                  : ''}
              </LoadingButton>
            ))}
            {switchError && (
              <Typography variant="caption" color="error" align="center">
                {t('account-blocked.switch-failed')}
              </Typography>
            )}
          </Stack>
        )}
        <Button variant="text" onClick={handleLogout}>
          {t('account-blocked.logout')}
        </Button>
      </Stack>
    </Box>
  );
}

function OutdatedSnackbar() {
  const { t } = useTranslation('translation');

  // Socket push is preferred (live). HTTP header path + focus/visibility
  // re-check so we still notify if the socket event was missed (e.g. other
  // EB instance, brief disconnect) or the tab was backgrounded.
  const [isOutDated, setOutdated] = useState(isDashboardOutdated);

  useEffect(() => {
    const sync = () => setOutdated(isDashboardOutdated());

    const handleSocketUpdate = (event) => {
      try {
        const incoming = parseInt(event?.detail?.latestVersion ?? 'NaN', 10);
        if (!Number.isNaN(incoming)) {
          localStorage.setItem(DASHBOARD_VERSION_LATEST_KEY, String(incoming));
        }
      } catch {
        // ignore client storage errors
      }
      sync();
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') sync();
    };

    const handleStorage = (event) => {
      if (
        event.key === DASHBOARD_VERSION_LATEST_KEY ||
        event.key === DASHBOARD_VERSION_BASELINE_KEY
      ) {
        sync();
      }
    };

    window.addEventListener('socket-dashboard-update', handleSocketUpdate);
    window.addEventListener('latest-dashboard-version', sync);
    window.addEventListener('focus', sync);
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('storage', handleStorage);
    sync();

    return () => {
      window.removeEventListener('socket-dashboard-update', handleSocketUpdate);
      window.removeEventListener('latest-dashboard-version', sync);
      window.removeEventListener('focus', sync);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  const handleRefresh = useCallback(() => {
    window.location.reload();
  }, []);

  if (!isOutDated) {
    return null;
  }

  return (
    <Snackbar
      open={isOutDated}
      autoHideDuration={null}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      sx={{
        bottom: { xs: 20, sm: 28 },
        left: 0,
        right: 0,
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        px: 2,
      }}
    >
      <Paper
        elevation={0}
        role="status"
        aria-live="polite"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          width: '100%',
          maxWidth: 420,
          px: 2,
          py: 1.5,
          borderRadius: 3.5,
          bgcolor: (theme) =>
            theme.palette.mode === 'light'
              ? alpha('#ffffff', 0.78)
              : alpha('#1c1c1e', 0.82),
          color: (theme) =>
            theme.palette.mode === 'light' ? '#1d1d1f' : '#f5f5f7',
          border: (theme) =>
            `1px solid ${
              theme.palette.mode === 'light'
                ? alpha('#000000', 0.06)
                : alpha('#ffffff', 0.1)
            }`,
          boxShadow: (theme) =>
            theme.palette.mode === 'light'
              ? `0 1px 2px ${alpha('#000', 0.04)}, 0 12px 40px ${alpha(
                  '#000',
                  0.1
                )}, 0 0 0 0.5px ${alpha('#000', 0.04)}`
              : `0 1px 2px ${alpha('#000', 0.3)}, 0 16px 48px ${alpha(
                  '#000',
                  0.45
                )}, 0 0 0 0.5px ${alpha('#fff', 0.06)}`,
          backdropFilter: 'blur(28px) saturate(180%)',
          WebkitBackdropFilter: 'blur(28px) saturate(180%)',
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            flexShrink: 0,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: (theme) =>
              theme.palette.mode === 'light' ? '#1d1d1f' : '#f5f5f7',
            bgcolor: (theme) =>
              theme.palette.mode === 'light'
                ? alpha('#000000', 0.05)
                : alpha('#ffffff', 0.1),
          }}
        >
          <RefreshRoundedIcon
            sx={{
              fontSize: 20,
              '@keyframes refreshSpin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' },
              },
              animation: 'refreshSpin 0.9s cubic-bezier(0.4, 0, 0.2, 1) 2',
              '@media (prefers-reduced-motion: reduce)': {
                animation: 'none',
              },
            }}
          />
        </Box>

        <Stack spacing={0.15} sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              letterSpacing: '-0.01em',
              lineHeight: 1.3,
            }}
          >
            {t('version-update.title')}
          </Typography>
          <Typography
            sx={{
              fontSize: 12.5,
              fontWeight: 400,
              lineHeight: 1.35,
              color: (theme) =>
                theme.palette.mode === 'light'
                  ? alpha('#1d1d1f', 0.56)
                  : alpha('#f5f5f7', 0.56),
            }}
          >
            {t('version-update.description')}
          </Typography>
        </Stack>

        <Button
          disableElevation
          onClick={handleRefresh}
          sx={{
            flexShrink: 0,
            minWidth: 0,
            borderRadius: 999,
            px: 1.75,
            py: 0.75,
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: '-0.01em',
            textTransform: 'none',
            whiteSpace: 'nowrap',
            color: (theme) =>
              theme.palette.mode === 'light' ? '#ffffff' : '#1d1d1f',
            bgcolor: (theme) =>
              theme.palette.mode === 'light' ? '#1d1d1f' : '#f5f5f7',
            boxShadow: 'none',
            transition: 'transform 0.15s ease, opacity 0.15s ease',
            '&:hover': {
              bgcolor: (theme) =>
                theme.palette.mode === 'light' ? '#000000' : '#ffffff',
              boxShadow: 'none',
            },
            '&:active': {
              transform: 'scale(0.97)',
            },
          }}
        >
          {t('version-update.action')}
        </Button>
      </Paper>
    </Snackbar>
  );
}

/**
 * Reload when the session token is changed, even in another tab.
 */
function useCrossTabTokenChange() {
  useEffect(() => {
    const handleTokenChange = (event) => {
      if (event.key === 'token') {
        window.location.reload();
      }
    };

    window.addEventListener('storage', handleTokenChange);

    return () => {
      window.removeEventListener('storage', handleTokenChange);
    };
  });
}

function App({ lang }) {
  useCrossTabTokenChange();

  useEffect(() => {
    return history.listen(() => {
      BProgress.start();
    });
  }, []);

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <QueryClientProvider client={queryClient}>
        <HelmetProvider>
          <ReduxProvider store={store}>
            <BrandProvider>
              <ThemeProvider>
                <MaintenanceGate>
                  <BrandGate>
                    <RtlLayout>
                      <LocalizationProvider
                        dateAdapter={AdapterDateFns}
                        adapterLocale={getDateFnsLocale(lang)}
                      >
                        <NotistackProvider>
                          <Router history={history}>
                            <JwtProvider>
                              <EventBusProvider>
                                <GeofenceProvider>
                                  <ScrollToTop />
                                  <SessionExpiredModal />
                                  <AccountBlockedScreen />
                                  {!isDevelopment && <OutdatedSnackbar />}
                                  {renderRoutes(routes)}
                                </GeofenceProvider>
                              </EventBusProvider>
                            </JwtProvider>
                          </Router>
                        </NotistackProvider>
                      </LocalizationProvider>
                    </RtlLayout>
                  </BrandGate>
                </MaintenanceGate>
              </ThemeProvider>
            </BrandProvider>
          </ReduxProvider>
        </HelmetProvider>
      </QueryClientProvider>
    </GoogleOAuthProvider>
  );
}

const AppMemoed = React.memo(App);

export default function AppLocalized() {
  const { i18n } = useTranslation('translation');

  return <AppMemoed lang={i18n?.language} />;
}
